import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { buildAppPath } from '@/utils/tenant';
import { useDispatch } from 'react-redux';
import { FiCheckCircle, FiXCircle } from 'react-icons/fi';
import Loader from 'wrapper/loader';
import { useToast } from '@/hooks/use-toast';
import apiClient from '../../services/apiClient';
import { setUserFromToken } from '../../redux/slices/authSlice';
import { setUserRole } from '../../redux/slices/userRole';
import { executeCallbackRouting, extractOrganizationContext } from '../../utils/callbackRouter';

const PlatformAuthCallback = () => {
  const { toast } = useToast();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const isProcessingRef = React.useRef(false);

  useEffect(() => {
    // Prevent duplicate calls (React StrictMode in dev can call this twice)
    // Using ref to persist across renders
    if (!isProcessingRef.current) {
      isProcessingRef.current = true;
      handleCallback();
    }
  }, []);

  const handleCallback = async () => {
    try {
      console.log('[PlatformAuthCallback] Starting callback processing');
      console.log('[PlatformAuthCallback] URL params:', window.location.search);
      console.log('[PlatformAuthCallback] All search params:', Object.fromEntries(searchParams.entries()));

      // First, check if we should be routed to a different callback handler
      const shouldRedirect = executeCallbackRouting(searchParams);
      if (shouldRedirect) {
        console.log('[PlatformAuthCallback] Redirecting via callback router');
        // Smart router is handling the redirect, so we exit early
        return;
      }

      // Extract and log organization context for debugging
      const orgContext = extractOrganizationContext(searchParams);
      console.log('[PlatformAuthCallback] Processing platform callback with org context:', orgContext);

      // Get the session token from URL params (from magic link authentication)
      const sessionToken = searchParams.get('session_token');
      const token = searchParams.get('token'); // Legacy fallback

      console.log('[PlatformAuthCallback] Tokens found:', {
        sessionToken: sessionToken ? 'YES' : 'NO',
        token: token ? 'YES' : 'NO'
      });

      if (!sessionToken && !token) {
        throw new Error('No authentication token found in URL');
      }

      let finalSessionToken = sessionToken;

      // If we have a Stytch magic link token, exchange it for a JWT
      if (token && !sessionToken) {
        const stytch_token_type = searchParams.get('stytch_token_type') || 'multi_tenant_magic_links';
        const response = await apiClient.post('/platform/session/exchange', {
          token,
          stytch_token_type
        });

        // The backend should return a JWT access token
        if (response.data.access_token) {
          const tokenManager = (await import('../../services/auth/tokenManager')).default;
          const sessionManager = (await import('../../services/session/SessionManager')).default;

          // Store the JWT token
          console.log('[PlatformAuthCallback] Storing JWT token');
          tokenManager.setToken(
            response.data.access_token,
            response.data.user,
            response.data.refresh_token
          );

          // Initialize session manager with the token
          sessionManager.storeTokenInMemory(response.data.access_token);
          sessionManager.initialize();

          // Update Redux state
          dispatch(setUserFromToken());
          dispatch(setUserRole(response.data.user?.role === 'PLATFORM_SUPER_ADMIN' ? 'superAdmin' : response.data.user?.role));

          // Final verification before redirect
          const finalCheck = tokenManager.getAccessToken();
          console.log('[PlatformAuthCallback] Final token check before redirect:', finalCheck ? 'TOKEN EXISTS' : 'NO TOKEN!');

          setStatus('success');
          toast({ title: 'Welcome to MIRA Platform Administration' });

          setTimeout(() => {
            navigate(buildAppPath('/platform/dashboard'));
          }, 1500);
          return;
        } else if (response.data.session_token) {
          // If backend returned a session token, we might need to exchange it for JWT
          finalSessionToken = response.data.session_token;
          console.warn('Backend returned session_token instead of JWT. Additional exchange may be needed.');
        }
      }

      if (finalSessionToken) {
        // Exchange session token for JWT
        try {
          // First, try to exchange the session token for a JWT
          const exchangeResponse = await apiClient.post('/platform/session/exchange', {
            session_token: finalSessionToken,
            stytch_token_type: 'session'
          });

          if (exchangeResponse.data.access_token) {
            const tokenManager = (await import('../../services/auth/tokenManager')).default;
            const sessionManager = (await import('../../services/session/SessionManager')).default;

            // Store the JWT token
            console.log('[PlatformAuthCallback] Storing JWT token (exchange)');
            tokenManager.setToken(
              exchangeResponse.data.access_token,
              exchangeResponse.data.user,
              exchangeResponse.data.refresh_token
            );

            // Also store in localStorage and sessionStorage for backward compatibility and redundancy
            // Initialize session manager
            sessionManager.storeTokenInMemory(exchangeResponse.data.access_token);
            sessionManager.initialize();

            // Update Redux state
            dispatch(setUserFromToken());
            dispatch(setUserRole(exchangeResponse.data.user?.role === 'PLATFORM_SUPER_ADMIN' ? 'superAdmin' : exchangeResponse.data.user?.role));

            setStatus('success');
            toast({ title: 'Welcome to MIRA Platform Administration' });

            // Redirect to dashboard after a brief delay
            setTimeout(() => {
              navigate(buildAppPath('/platform/dashboard'));
            }, 1500);
            return;
          }
        } catch (exchangeError) {
          console.log('Session token exchange failed, trying verification:', exchangeError);

          // Fallback: Verify the session token and get user data
          const verifyResponse = await apiClient.post('/auth/session/verify', {
            session_token: finalSessionToken
          });

          if (verifyResponse.data.success && verifyResponse.data.data?.user) {
            const userData = verifyResponse.data.data.user;

            // Store session token and user data (not ideal, but a fallback)
            console.log('[PlatformAuthCallback] Storing session token (fallback)');
            const tokenManager = (await import('../../services/auth/tokenManager')).default;
            tokenManager.setToken(finalSessionToken, userData, null, true); // true = isSessionToken

            // Update Redux state
            dispatch(setUserFromToken());
            dispatch(setUserRole(userData.role === 'PLATFORM_SUPER_ADMIN' ? 'superAdmin' : userData.role));

            setStatus('success');
            toast({ title: 'Welcome to MIRA Platform Administration' });

            // Redirect to dashboard after a brief delay
            setTimeout(() => {
              navigate(buildAppPath('/platform/dashboard'));
            }, 1500);
          } else {
            throw new Error('Invalid session or user data');
          }
        }
      } else {
        throw new Error('No valid authentication token received');
      }
    } catch (error) {
      console.error('Auth callback error:', error);

      const message = error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        'Authentication failed';

      setStatus('error');
      setErrorMessage(message);
      toast({ title: message, variant: "destructive" });

      // Redirect to login after delay
      setTimeout(() => {
        navigate(buildAppPath('/platform/login'));
      }, 3000);
    }
  };

  return (
    <div className="h-screen flex items-center justify-center bg-gray-50">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
        {status === 'processing' && (
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <Loader FullScreen={false} variant="colorful" />
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Authenticating...
            </h2>
            <p className="text-gray-600">
              Please wait while we verify your credentials
            </p>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center">
            <FiCheckCircle className="text-green-500 mx-auto mb-4" size={48} />
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Authentication Successful!
            </h2>
            <p className="text-gray-600">
              Redirecting to platform dashboard...
            </p>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center">
            <FiXCircle className="text-red-500 mx-auto mb-4" size={48} />
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Authentication Failed
            </h2>
            <p className="text-gray-600 mb-4">
              {errorMessage}
            </p>
            <p className="text-sm text-gray-500">
              Redirecting to login page...
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlatformAuthCallback;
